import { useEffect, useRef, useState } from 'react';
import ModalWarning from './ModalWarning';
import DraggableWarning from './DraggableWarning';
import Overlay from '@cp/src/components/Overlay/Overlay';
import { Countdown } from '@cp/src/components/Countdown/Countdown';
import {
  COSMOS_SYNC_WAITING_FOR_CALL_BEFORE_BLOCKING,
  COUNTDOWN_INTERVAL_AFTER_SLASH_CALL_ENDED_BEFORE_BLOCKING,
  COUNTDOWN_INTERVAL_BEFORE_BLOCKING
} from '@cp/src/constants/Common.constants';
import { useDispatch, useSelector } from 'react-redux';
import {
  CosmosSyncBlockStatus,
  setCosmosSyncBlockStatus,
  SlashCallStatus
} from '@cp/reducers/commonSlice';
import { CALL_BRIDGE_STATUSES } from '@cp/pages/CaseDetails/feedbackForm/interfaces/index.type';
import { RootState } from '@cp/src/store';
import { ExtensionHandler } from '@cp/utils/extension.utils';
import { useLocation, useNavigate } from 'react-router-dom';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { AgentTrackingEvents } from '@cp/src/service/clickStream.constant';
import { LocalStorage } from '@cp/src/utils/StorageUtils';
import logger from '@cp/src/utils/logger';

const LonghornWarning = () => {
  const [isDraggableWarningVisible, setIsDraggableWarningVisible] = useState(false);
  const [timerResetAfterCall, setTimerResetAfterCall] = useState(false);
  const lastSyncTimeStamp = useSelector((state: RootState) => state.common.lastSyncTimestamp);
  const timeRemaining = Date.now() - lastSyncTimeStamp;
  const timeRemainingInSeconds = Number((timeRemaining / 1000).toFixed(0));
  const initialRemainingTime = COUNTDOWN_INTERVAL_BEFORE_BLOCKING - timeRemainingInSeconds;
  const [localTimeLeft, setLocalTimeLeft] = useState(initialRemainingTime);
  const dispatch = useDispatch();
  const location = useLocation();
  const ameyoCallData = useSelector((store: RootState) => store.ameyoSlice.ameyoCallData);
  const isSlashCallActive =
    useSelector((store: RootState) => store.common.slashCallStatusFromExtension) ===
    SlashCallStatus.ANSWERED;
  const isCallOnGoing =
    Boolean(ameyoCallData.ringingAt || ameyoCallData.acceptedAt) &&
    !ameyoCallData.disconnectedAt &&
    isSlashCallActive;

  const timer = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  const handleOnComplete = () => {
    if (isCallOnGoing) {
      timer.current = setTimeout(() => {
        handleOnComplete();
        logger('waiting for call to end ameyo');
      }, COSMOS_SYNC_WAITING_FOR_CALL_BEFORE_BLOCKING);
      return;
    }
    if (localTimeLeft <= 0 && isSlashCallActive) {
      logger('unable to block as user is on call');
      setTimerResetAfterCall(true);
      return;
    }

    if (!isSlashCallActive && localTimeLeft <= 0) {
      logger("Blocking longhorn as user didn't take any action slash");
      LocalStorage.setItem('lastRouteBeforeOffline', location.pathname);
      navigate('/');
      dispatch(setCosmosSyncBlockStatus(CosmosSyncBlockStatus.BLOCKED));
      ExtensionHandler.setAvailability(false);
      ExtensionHandler.blockSlash();
    }
  };

  useEffect(() => {
    if (!isSlashCallActive && timerResetAfterCall) {
      setLocalTimeLeft(COUNTDOWN_INTERVAL_AFTER_SLASH_CALL_ENDED_BEFORE_BLOCKING);
    }
  }, [isSlashCallActive]);

  useEffect(() => {
    addClickstreamEvent(AgentTrackingEvents.LH_COSMOS_SYNC_WARNING_MODAL_LOADED);
  }, []);

  useEffect(() => {
    if (localTimeLeft <= 0) {
      dispatch(setCosmosSyncBlockStatus(CosmosSyncBlockStatus.BLOCKED));
    }
  }, [localTimeLeft]);

  return (
    <>
      {isDraggableWarningVisible ? (
        localTimeLeft > 0 ? (
          <DraggableWarning timeLeft={localTimeLeft} />
        ) : isSlashCallActive ? (
          <DraggableWarning timeLeft={localTimeLeft} inCall />
        ) : (
          <DraggableWarning timeLeft={localTimeLeft} />
        )
      ) : (
        <Overlay visible={!isDraggableWarningVisible}>
          <ModalWarning
            handleOnClose={() => setIsDraggableWarningVisible(true)}
            timeLeft={localTimeLeft}
          />
        </Overlay>
      )}
      <Countdown
        key={localTimeLeft}
        startFrom={localTimeLeft}
        stopAt={0}
        interval={1000}
        decrementBy={1}
        onComplete={handleOnComplete}
        step={current => setLocalTimeLeft(current)}
        formatter="mm:ss"
      />
    </>
  );
};

export default LonghornWarning;
